<template>
    <div>
        <ContentBody
            v-if="!isPendingStudent"
            class="student-content"
        >
            <template #contentBodyExam>
                <div class="student-content__exam-name-container">
                    <ExamVersionDropdown class="student-content__exam" />
                </div>
            </template>
            <template #contentBodyTitle>
                <div class="student-content__student-name">
                    <SkeletonLoader
                        v-if="studentStats.isLoading"
                        class="student-content__loader"
                    />
                    <div
                        v-else
                        class="student-content__first-name"
                    >
                        {{ currentStudentName.firstName }}
                    </div>
                    <SkeletonLoader
                        v-if="studentStats.isLoading"
                        class="student-content__loader"
                    />
                    <div
                        v-else
                        class="student-content__last-name"
                    >
                        {{ currentStudentName.lastName }}
                    </div>
                </div>
            </template>
            <template #contentTabs>
                <div
                    class="student-content__tabs"
                    role="tablist"
                >
                    <ContentTab
                        key="Answered"
                        :is-active="activeTab === 'Answered'"
                        label="Answered"
                        :count="questions['Answered']?.length"
                        role="tab"
                        :aria-selected="activeTab === 'Answered' ? 'true' : 'false'"
                        @keydown.enter="activeTab = 'Answered'"
                        @click="clickChangeTab('Answered')"
                    />
                    <ContentTab
                        key="Flagged"
                        :is-active="activeTab === 'Flagged'"
                        label="Flagged"
                        :count="questions['Flagged']?.length"
                        role="tab"
                        :aria-selected="activeTab === 'Flagged' ? 'true' : 'false'"
                        @keydown.enter="activeTab = 'Flagged'"
                        @click="clickChangeTab('Flagged')"
                    />
                    <ContentTab
                        key="Incorrect"
                        :is-active="activeTab === 'Incorrect'"
                        label="Incorrect"
                        :count="questions['Incorrect']?.length"
                        role="tab"
                        :aria-selected="activeTab === 'Incorrect' ? 'true' : 'false'"
                        @keydown.enter="activeTab = 'Incorrect'"
                        @click="clickChangeTab('Incorrect')"
                    />
                    <ContentTab
                        key="Correct"
                        :is-active="activeTab === 'Correct'"
                        label="Correct"
                        :count="questions['Correct']?.length"
                        role="tab"
                        :aria-selected="activeTab === 'Correct' ? 'true' : 'false'"
                        @keydown.enter="activeTab = 'Correct'"
                        @click="clickChangeTab('Correct')"
                    />
                </div>
            </template>
            <template #contentSearch>
                <div class="student-content__search">
                    <div class="student-content__search-container">
                        <Search
                            class="student-content__search-questions"
                            v-model="searchString"
                            placeholder="Search Questions"
                            type="white"
                            @input="setFilterOptions"
                        />
                    </div>
                    <FilterDropdown
                        class="student-content__filter"
                        size="large"
                        filter-label="Filter by Subject"
                        :checkboxEntries="checkboxOptions"
                        :radioEntries="[]"
                    />
                </div>
                <div v-if="enabledFilterSubjects.length" class="student-content__filters">
                    <div class="student-content__filters-label">
                        Filters:
                    </div>
                    <Pill
                        v-for="filter in enabledFilterSubjects"
                        :key="filter"
                        class="student-content__filters-pill"
                        :show-close="true"
                        @click="removeFilter(filter)"
                    >
                        {{ filter }}
                    </Pill>
                </div>
            </template>
            <template #contentQuestions>
                <div
                    v-if="!isPageLoading"
                >
                    <div
                        v-if="questions[activeTab]?.length === 0 && searchString !== ''"
                        class="student-content__no-activity"
                    >
                        <EmptyState
                            class="student-content__empty-state"
                            :title="emptyStateTitle"
                            message="Only question text is searchable. Check your spelling or try again."
                        >
                            <template #imageRow>
                                <div />
                            </template>
                        </EmptyState>
                    </div>
                    <div
                        v-if="questions['Flagged']?.length === 0 && activeTab === 'Flagged' && searchString === ''"
                        class="student-content__no-activity"
                    >
                        <EmptyState
                            class="student-content__empty-state"
                            state="flagged"
                            title="0 Flagged Questions"
                            message="Questions flagged by this student will appear here."
                        />
                    </div>
                    <div
                        v-if="questions['Answered']?.length === 0 && activeTab === 'Answered' && searchString === ''"
                        class="student-content__no-activity"
                    >
                        <EmptyState
                            class="student-content__empty-state"
                            state="answered"
                            title="0 Answered Questions"
                            message="Questions answered by this student will appear here."
                        />
                    </div>
                    <div
                        v-if="questions['Incorrect']?.length === 0 && activeTab === 'Incorrect' && searchString === ''"
                        class="student-content__no-activity"
                    >
                        <EmptyState
                            class="student-content__empty-state"
                            state="incorrect"
                            title="0 Incorrect Questions"
                            message="Questions answered incorrectly by this student will appear here."
                        />
                    </div>
                    <div
                        v-if="questions['Correct']?.length === 0 && activeTab === 'Correct' && searchString === ''"
                        class="student-content__no-activity"
                    >
                        <EmptyState
                            class="student-content__empty-state"
                            state="correct"
                            title="0 Correct Questions"
                            message="Questions answered correctly by this student will appear here."
                        />
                    </div>
                    <Table
                        v-else-if="!isLoading && questions[activeTab]?.length"
                        class="student-content__table-all"
                        theme="open"
                        :rows="getProcessedRows(questions[activeTab] || [])"
                        :columns="tableColumns"
                        :show-header="false"
                        :table-content-styles="{
                            boxSizing: 'none',
                        }"
                        :table-column-labels-styles="{
                            display: 'flex',
                            justifyContent: 'space-between',
                            fontSize: '12px',
                            color: '#587183',
                            letterSpacing: '0.22px',
                            lineHeight: '14px',
                            paddingRight: '14px'
                        }"
                        :table-row-styles="{
                            display: 'flex',
                            justifyContent: 'space-between',
                            padding: '12px 0 12px 12px',
                            cursor: 'pointer',
                        }"
                        @rowClicked="rowClicked({serial: $event.serial as string})"
                    >
                        <template #tableCellValue="{ row, column }">
                            <StudentQuestionRow
                                class="student-content__question-row"
                                :row="row"
                                :column="column"
                                :num-of-questions="questions[activeTab]?.length"
                            />
                        </template>
                    </Table>
                </div>
                <Icon
                    v-else
                    type="loading2"
                    class="student-content__loading"
                />
            </template>
            <template #contentPagination>
                <TablePagination
                    v-if="questions[activeTab]?.length || 0 > 0"
                    class="student-content__pagination"
                    unit="Questions"
                    :per-page="perPage"
                    :current-page="currentPage"
                    :total="questions[activeTab]?.length || 0"
                    @changePage="changePage($event)"
                />
            </template>
        </ContentBody>
        <div
            v-else
        >
            <PendingStudent />
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { onBeforeRouteUpdate, useRoute, useRouter } from 'vue-router'
import UIKit from '@pocketprep/ui-kit'
import BrandColors from '@pocketprep/ui-kit/pocketprep-export.module.scss'
import PendingStudent from '@/components/PendingStudent.vue'
import ContentBody from '@/components/ContentBody.vue'
import StudentQuestionRow from '@/components/StudentQuestionRow.vue'
import ContentTab from '@/components/Content/ContentTab.vue'
import { groupModule } from '@/store/group/module'
import { studentModule } from '@/store/student/module'
import { statsModule } from '@/store/stats/module'
import { examModule } from '@/store/exam/module'
import { userModule } from '@/store/user/module'
import type { Study } from '@pocketprep/types'
import { organizationModule } from '@/store/organization/module'
import { stripHtmlTags } from '@/utils'
import ExamVersionDropdown from '@/components/ExamVersionDropdown.vue'
import { analyticsModule } from '@/store/analytics/module'
import { licenseModule } from '@/store/license/module'
import type { TStudentQuestionRow, TStudentQuestions } from '@/store/types'

const {
    Icon,
    Search,
    FilterDropdown,
    Table,
    Pill,
    TablePagination,
    EmptyState,
    SkeletonLoader,
} = UIKit

type TSubjectOption = {
    id: string
    label: string
    enabled: boolean
}

const route = useRoute()
const router = useRouter()

// Colors (for use in template)
const brandColors = BrandColors

// Tab control
const activeTab = ref('Answered')

// Search
const searchString = ref('')

// Filter options
const checkboxOptions = ref<TSubjectOption[]>([{
    id: 'test',
    label: 'label',
    enabled: false,
}])

// Pagination
const currentPage = ref(0)
const perPage = ref(20)

// Table
const rowHoverId = ref<string | null>(null)
const tableColumns = [{
    name: ' ',
    propName: 'question',
    isSortDisabled: true,
}, {
    name: ' ',
    propName: 'numStudentFlagged',
    isSortDisabled: true,
}]

const isLoading = ref(false)

const studentStats = computed(() => {
    return statsModule.getters.getCurrentStudentStats()
})

const currentStudentName = computed(() => {
    const currentStudent = studentModule.getters.getCurrentStudent()

    if (currentStudent?.displayFirstName || currentStudent?.displayLastName) {
        return {
            firstName: currentStudent?.displayFirstName,
            lastName: currentStudent?.displayLastName,
        }
    } else {
        return {
            firstName: studentStats.value.userInfo.firstName || '',
            lastName: studentStats.value.userInfo.lastName || '',
        }
    }
})

const isPageLoading = computed(() => {
    // Combo of component loading and fetcher loading
    return studentStats.value.isLoading && isLoading.value
})

const studentFlaggedQuestions = computed(() => {
    return studentStats.value.flaggedQuestions.filter(q => !q.isMockQuestion)
})

const studentAnsweredQuestions = computed(() => {
    return studentStats.value.answeredQuestions
})

const enabledFilterSubjects = computed(() => {
    return checkboxOptions.value.reduce<string[]>((acc, s) => {
        if (s.enabled) {
            let label = s.label
            if (s.label.includes('(')) {
                /*
                    Have to trim the count out the label before adding it to the filter subjects structure
                    (if it has a count)
                */
                label = s.label.split('(')[0]?.slice(0, -1) || ''
            }
            acc.push(label)
        }
        return acc
    }, [])
})

const currentOrgLicenses = computed(() => {
    return licenseModule.getters.getCurrentOrgLicenses()
})

const currentOrgPendingLicenses = computed(() => {
    return currentOrgLicenses.value.filter(l => l.status === 'pending')
})

const isPendingStudent = computed(() => {
    const routeStudentId = route.params.studentId
    const isCurrentStudentPending = !!currentOrgPendingLicenses.value
        .find(opl => opl.orgStudent?.objectId === routeStudentId)
    return route.query.isPending === 'true' || isCurrentStudentPending
})

const isQuestionFlagged = (serial: string) => {
    return !!studentFlaggedQuestions.value.find(question => question.serial === serial)
}

const studentQuestions = () => {
    /*
        This function builds out a list of questions (both answered and flagged) for a given student. We have to do
        this because we want one list to build answered / flagged questions from so we don't duplicate code related
        to searching.
    */
    const studentQuestionsList: TStudentQuestionRow[] = []

    // First, let's do studentAnsweredQuestions
    studentAnsweredQuestions.value.forEach(answeredQuestion => {
        const subject = answeredQuestion.question.subject as Study.Class.SubjectJSON
        const scenarioPartLabel = examModule.getters.getScenarioPartLabel(answeredQuestion.question)
        studentQuestionsList.push({
            id: Math.random(),
            serial: answeredQuestion.question.serial,
            subject: subject.name,
            prompt: answeredQuestion.question.prompt.replace(/<[^\s>]+[^>]*>/gi, ' '),
            isFlagged: isQuestionFlagged(answeredQuestion.question.serial),
            isCorrect: answeredQuestion.answer.isCorrect,
            isAnswered: true,
            tabIndex: 0,
            scenarioPartLabel: scenarioPartLabel,
        })
    })

    // Now, check flaggedQuestions and skip over any dupes (same serial)
    studentFlaggedQuestions.value.forEach(flaggedQuestion => {
        if (!studentQuestionsList.find(question => question.serial === flaggedQuestion.serial)) {
            studentQuestionsList.push({
                id: Math.random(),
                serial: flaggedQuestion.serial,
                subject: flaggedQuestion.subject,
                prompt: flaggedQuestion.prompt,
                isFlagged: true,
                isCorrect: flaggedQuestion.answeredCorrectly,
                isAnswered: false, // know it's false because we have already added the answered flagged ?s
                tabIndex: 0,
                scenarioPartLabel: flaggedQuestion.scenarioPartLabel || '',
            })
        }
    })

    return studentQuestionsList
}

const examQuestionsNoMockSerialSet = computed(() => {
    return new Set(examModule.getters.getCurrentExamQuestionsNoMock().map(q => q.serial))
})

const questions = computed(() => {
    // regex escape from https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions
    const promptRegex = new RegExp(searchString.value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi')

    return studentQuestions().reduce<TStudentQuestions>((acc, question) => {
        const serial = question.serial
        if (!examQuestionsNoMockSerialSet.value.has(serial)) {
            return acc
        }

        const isFlagged = isQuestionFlagged(serial)
        const isAnswered = question.isAnswered
        const isCorrect = question.isCorrect
        const subject = question.subject
        let prompt = stripHtmlTags(question.prompt)
        if (searchString.value && !prompt.toLowerCase().includes(searchString.value.toLowerCase())) {
            return acc
        } else if (searchString.value) {
            prompt = prompt.replace(promptRegex, '<span>$&</span>')
        }
        if (enabledFilterSubjects.value.length && !enabledFilterSubjects.value.includes(subject)) {
            return acc
        }
        const questionRow = {
            id: Math.random(),
            serial,
            subject,
            prompt,
            isFlagged,
            isCorrect,
            isAnswered,
            tabIndex: 0,
            scenarioPartLabel: question.scenarioPartLabel,
        }

        if (isFlagged) {
            acc.Flagged?.push(questionRow)
        }

        if (isAnswered) {
            acc.Answered?.push(questionRow)
        }

        if (!isCorrect && isAnswered) {
            acc.Incorrect?.push(questionRow)
        }

        if (isCorrect) {
            acc.Correct?.push(questionRow)
        }
        return acc
    }, { Answered: [], Flagged: [], Incorrect: [], Correct: [] })
})

const currentExamMetadata = computed(() => {
    return examModule.getters.getCurrentExamMetadata()
})

const emptyStateTitle = computed(() => {
    return `No results for "${searchString.value}"`
})

const getQuestionCountForSubject = (subjectName: string) => {
    const promptRegex = new RegExp(searchString.value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi')
    const qs = studentQuestions().reduce<TStudentQuestions>((acc, question) => {
        const serial = question.serial
        const isFlagged = isQuestionFlagged(serial)
        const isAnswered = question.isAnswered
        const isCorrect = question.isCorrect
        const subject = question.subject
        let prompt = question.prompt
        if (searchString.value && !prompt.toLowerCase().includes(searchString.value.toLowerCase())) {
            return acc
        } else if (searchString.value) {
            prompt = prompt.replace(promptRegex, '<span>$&</span>')
        }

        const questionRow = {
            id: Math.random(),
            serial,
            subject,
            prompt,
            isFlagged,
            isCorrect,
            isAnswered,
            tabIndex: 0,
        }

        if (isFlagged) {
            acc.Flagged?.push(questionRow)
        }

        if (isAnswered) {
            acc.Answered?.push(questionRow)
        }

        if (!isCorrect && isAnswered) {
            acc.Incorrect?.push(questionRow)
        }

        if (isCorrect) {
            acc.Correct?.push(questionRow)
        }
        return acc
    }, { Answered: [], Flagged: [], Incorrect: [], Correct: [] })
    return qs[activeTab.value]?.filter((s: TStudentQuestionRow) => s.subject === subjectName).length
}

const changePage = (newPage: number) => {
    currentPage.value = newPage

    // Move to top of page
    scrollTop()
}

const scrollTop = () => {
    const contentEl = document.querySelector('.content-body-exam')
    contentEl?.scrollIntoView()
}

const getProcessedRows = (rows: TStudentQuestionRow[]) => {
    return rows.map(row => {
        const newRow = { ...row }
        if (newRow.id === rowHoverId.value) {
            newRow.styles = {
                backgroundColor: `${brandColors.grayBackground}60`,
            }
        }
        return newRow
    }).slice(currentPage.value * perPage.value, (currentPage.value * perPage.value) + perPage.value)
}

const clickChangeTab = async (tab: 'Answered' | 'Flagged' | 'Incorrect' | 'Correct') => {
    // Swap to clicked tab
    activeTab.value = tab

    // Reset pagination to 0
    currentPage.value = 0

    // Reload filter options
    setFilterOptions()

    if (tab === 'Flagged') {
        await userModule.actions.trackEvent('Student_Flagged_Questions')
    }

    analyticsModule.actions.amplitudeTrack('Tab Clicked', {
        tabContext: 'Student Content',
        tabName: tab,
    })
}

const isFilterEnabled = (subject: string) => {
    const checkboxIndex = checkboxOptions.value.findIndex(s => s.id === subject && s.enabled === true)
    if (checkboxIndex > -1) {
        return true
    }
    return false
}

const setFilterOptions = (enabledFilterOption?: string | null) => {
    const examMetadataVal = currentExamMetadata.value
    checkboxOptions.value = Object.values(examMetadataVal?.knowledgeAreas || {}).map(subject => ({
        id: subject.name,
        label: `${subject.name} (${getQuestionCountForSubject(subject.name)})`,
        enabled: isFilterEnabled(subject.name) || enabledFilterOption === subject.name,
    })).sort((a, b) => a.id > b.id ? 1 : -1) // Sort alphabetically / numerically
}

const removeFilter = (filterId: string) => {
    checkboxOptions.value = checkboxOptions.value.map(o => o.id === filterId ? { ...o, enabled: false } : o)
}

const rowClicked = (row: { serial: string }) => {
    if (activeTab.value === 'Flagged') {
        const clickedQuestion = questions.value.Flagged?.find((q: TStudentQuestionRow) => {
            return q.serial === row.serial
        })

        if (clickedQuestion?.serial) {
            if (enabledFilterSubjects.value?.length) {
                router.push({
                    name: route.name as string,
                    params: { ...route.params },
                    query: {
                        questionSerial: clickedQuestion?.serial,
                        type: 'flagged',
                        subjects: enabledFilterSubjects.value,
                    },
                })
            } else {
                router.push({
                    name: route.name as string,
                    params: { ...route.params },
                    query: {
                        questionSerial: clickedQuestion?.serial,
                        type: 'flagged',
                    },
                })
            }
        }
    }

    if (activeTab.value === 'Answered') {
        const clickedQuestion = questions.value.Answered?.find((q: TStudentQuestionRow) => {
            return q.serial === row.serial
        })

        if (enabledFilterSubjects.value?.length) {
            router.push({
                name: route.name as string,
                params: { ...route.params },
                query: {
                    questionSerial: clickedQuestion?.serial,
                    type: 'answered',
                    subjects: enabledFilterSubjects.value,
                },
            })
        } else {
            router.push({
                name: route.name as string,
                params: { ...route.params },
                query: {
                    questionSerial: clickedQuestion?.serial,
                    type: 'answered',
                },
            })
        }
    }

    if (activeTab.value === 'Incorrect') {
        const clickedQuestion = questions.value.Incorrect?.find((q: TStudentQuestionRow) => {
            return q.serial === row.serial
        })

        if (enabledFilterSubjects.value?.length) {
            router.push({
                name: route.name as string,
                params: { ...route.params },
                query: {
                    questionSerial: clickedQuestion?.serial,
                    type: 'incorrect',
                    subjects: enabledFilterSubjects.value,
                },
            })
        } else {
            router.push({
                name: route.name as string,
                params: { ...route.params },
                query: {
                    questionSerial: clickedQuestion?.serial,
                    type: 'incorrect',
                },
            })
        }
    }

    if (activeTab.value === 'Correct') {
        const clickedQuestion = questions.value.Correct?.find((q: TStudentQuestionRow) => {
            return q.serial === row.serial
        })

        if (enabledFilterSubjects.value?.length) {
            router.push({
                name: route.name as string,
                params: { ...route.params },
                query: {
                    questionSerial: clickedQuestion?.serial,
                    type: 'correct',
                    subjects: enabledFilterSubjects.value,
                },
            })
        } else {
            router.push({
                name: route.name as string,
                params: { ...route.params },
                query: {
                    questionSerial: clickedQuestion?.serial,
                    type: 'correct',
                },
            })
        }
    }
}

onBeforeRouteUpdate((to, from, next) => {
    groupModule.actions.updateCurrentGroupId(to.params.groupId as string)
    studentModule.actions.updateCurrentStudentId(to.params.studentId as string)
    setFilterOptions()
    next()
})

onMounted(async () => {
    isLoading.value = true
    const routeGroupId = route.params.groupId
    const routeStudentId = route.params.studentId

    // Checking the tab qs param
    if (route.query?.tab === 'flagged') {
        activeTab.value = 'Flagged'
        await userModule.actions.trackEvent('Student_Flagged_Questions')
    } else if (route.query?.tab === 'answered') {
        activeTab.value = 'Answered'
    }

    // Want to pull questions first since they are independent of student info
    const allExamMetadata = await examModule.actions.fetchExamMetadata()
    const currentExam = await examModule.actions.fetchCurrentGroupExam()
    const currentEM = currentExam?.examMetadata.objectId
        && allExamMetadata[currentExam.examMetadata.objectId]
    const currentOrganization = await organizationModule.actions.fetchCurrentOrganization()

    if (currentEM && currentOrganization) {
        await examModule.actions.fetchQuestionsByExam({
            compositeKey: currentEM.compositeKey,
            organizationId: currentOrganization.objectId,
            examMetadataId: currentEM.objectId,
        })
    }

    await Promise.all([
        groupModule.actions.updateCurrentGroupId(routeGroupId as string),
        studentModule.actions.updateCurrentStudentId(routeStudentId as string),
        licenseModule.actions.fetchCurrentOrgLicenses(),
    ])

    // Set options for filtering
    setFilterOptions()
    isLoading.value = false
})

watch(enabledFilterSubjects, (newVal, oldVal) => {
    const numNewEnabledFilters = newVal.length
    const filtersDidChange = newVal.length !== oldVal.length
    if (filtersDidChange && !isLoading.value) {
        analyticsModule.actions.amplitudeTrack('Table Filtered', {
            tableName: 'Student Content',
            didFilterSubject: true,
            subjectFiltersCount: numNewEnabledFilters,
        })
    }
}, { deep: true })
</script>

<style lang="scss" scoped>


.student-content {
    :deep(*) {
        .uikit-table__rows:nth-child(2) {
            min-height: 375px;
            max-height: 66.5vh; // only make it so that the questions are scrollable
        }
    }

    &__exam-name-container {
        display: flex;
        align-items: center;
        position: relative
    }

    &__loader {
        width: 160px;
        height: 24px;
        margin-bottom: 10px;
    }

    &__tabs {
        display: flex;
        padding-top: 65px;
    }

    &__filter {
        position: relative;
        width: 230px;
    }

    &__filters {
        margin: 16px 0 -16px 0;
    }

    &__filters-label {
        font-size: 13px;
        line-height: 22px;
        color: $slate;
        margin-right: 9px;
        display: inline-block;
        margin-left: 12px;
        vertical-align: top;
    }

    &__no-activity {
        text-align: center;
        padding: 0 42px 0 41px;
        margin-top: 54px;
    }

    &__empty-state {
        margin: 0 auto;
    }

    &__search {
        display: flex;
        margin: 26px auto 0;
        align-items: center;
    }

    &__search-container {
        width: 359px;
        margin-right: 20px;

        @include breakpoint(black-bear) {
            margin-right: 14px;
        }
    }

    &__pagination {
        margin: 17px 0;
        float: right;
    }

    &__loading {
        margin: 80px auto;
    }
}
</style>
